import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './new-form-document-modal.html';
import type { FormDefinition, FormFieldDefinition, Tag } from '../services/api.types';
import {
  createDocument,
  listForms,
  listTags,
  uploadFile,
} from '../services/api.service';
import {
  autoTagNamesForFormData,
  formValuesHaveChanges,
} from '../services/form-submission.service';
import { ConfirmDialog } from './confirm-dialog';

@customElement({ name: 'new-form-document-modal', template })
export class NewFormDocumentModal {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public saved!: () => void;
  @bindable public closed!: () => void;

  public showFormChooserModal = true;
  public showFormEntryModal = false;
  public formEntryLoading = false;
  public formEntrySaving = false;
  public formEntryError = '';
  public availableForms: FormDefinition[] = [];
  public formEntryTags: Tag[] = [];
  public selectedFormId = '';
  public formChooserSearch = '';
  public formChooserSuggestionsOpen = false;
  public formChooserHighlightedIndex = -1;
  public formValues: Record<string, unknown> = {};

  private _keyHandler: ((event: KeyboardEvent) => void) | null = null;
  private _initialFormValues: Record<string, unknown> = {};
  private _closeConfirmationOpen = false;

  public get selectedForm(): FormDefinition | null {
    return this.availableForms.find(form => form.id === this.selectedFormId) ?? null;
  }

  public get selectedFormFields(): FormFieldDefinition[] {
    return this.selectedForm?.fields.slice().sort((a, b) => a.position - b.position) ?? [];
  }

  public get filteredAvailableForms(): FormDefinition[] {
    const search = this.formChooserSearch.trim().toLocaleLowerCase();
    if (!search) return this.availableForms;
    return this.availableForms.filter(form =>
      form.title.toLocaleLowerCase().includes(search));
  }

  public get activeFormSuggestionId(): string {
    const form = this.filteredAvailableForms[this.formChooserHighlightedIndex];
    return form ? `new-document-form-suggestion-${form.id}` : '';
  }

  public get hasFormEntryChanges(): boolean {
    return this.showFormEntryModal &&
      formValuesHaveChanges(this.formValues, this._initialFormValues);
  }

  public binding(): void {
    void this.loadForms();
  }

  public attached(): void {
    this._keyHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') void this.close();
    };
    document.addEventListener('keydown', this._keyHandler);
  }

  public detached(): void {
    if (this._keyHandler)
      document.removeEventListener('keydown', this._keyHandler);
  }

  public async close(): Promise<void> {
    if (this.formEntrySaving || this._closeConfirmationOpen) return;
    if (this.hasFormEntryChanges) {
      this._closeConfirmationOpen = true;
      try {
        const discard = await ConfirmDialog.confirm(
          this.i18n.tr('documents.form.unsavedChanges'),
          this.i18n.tr('documents.form.unsavedChangesTitle'),
        );
        if (!discard) return;
      } finally {
        this._closeConfirmationOpen = false;
      }
    }
    this.closed();
  }

  public async loadForms(): Promise<void> {
    this.formEntryLoading = true;
    this.formEntryError = '';
    try {
      const forms = await listForms();
      this.availableForms = forms.filter(form => form.isActive !== false);
    } catch (error: unknown) {
      this.formEntryError = error instanceof Error
        ? error.message
        : this.i18n.tr('documents.form.formEntry.loadFailed');
    } finally {
      this.formEntryLoading = false;
    }
  }

  public openFormChooserSuggestions(): void {
    if (!this.formEntryLoading && this.availableForms.length > 0) {
      this.formChooserSuggestionsOpen = true;
    }
  }

  public toggleFormChooserSuggestions(): void {
    if (!this.formEntryLoading && this.availableForms.length > 0) {
      this.formChooserSuggestionsOpen = !this.formChooserSuggestionsOpen;
      this.formChooserHighlightedIndex = -1;
    }
  }

  public onFormChooserSearchInput(): void {
    this.formChooserSuggestionsOpen = true;
    this.selectedFormId = '';
    this.formChooserHighlightedIndex = -1;
  }

  public onFormChooserKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (this.formEntryLoading || this.filteredAvailableForms.length === 0) return;

      event.preventDefault();
      this.formChooserSuggestionsOpen = true;
      const forms = this.filteredAvailableForms;
      const currentIndex = this.formChooserHighlightedIndex;
      if (event.key === 'ArrowDown') {
        this.formChooserHighlightedIndex =
          currentIndex < 0 || currentIndex >= forms.length - 1
            ? 0
            : currentIndex + 1;
      } else {
        this.formChooserHighlightedIndex =
          currentIndex <= 0 || currentIndex >= forms.length
            ? forms.length - 1
            : currentIndex - 1;
      }
      return;
    }

    if (event.key === 'Enter' &&
        this.formChooserSuggestionsOpen &&
        this.formChooserHighlightedIndex >= 0) {
      const form = this.filteredAvailableForms[this.formChooserHighlightedIndex];
      if (form) {
        event.preventDefault();
        this.selectFormSuggestion(form);
      }
      return;
    }

    if (event.key === 'Enter' &&
        !this.formChooserSuggestionsOpen &&
        this.selectedForm) {
      event.preventDefault();
      void this.beginSelectedFormEntry();
      return;
    }

    if (event.key === 'Escape' && this.formChooserSuggestionsOpen) {
      event.preventDefault();
      this.formChooserSuggestionsOpen = false;
      this.formChooserHighlightedIndex = -1;
    }
  }

  public selectFormSuggestion(form: FormDefinition): void {
    this.selectedFormId = form.id;
    this.formChooserSearch = form.title;
    this.formChooserSuggestionsOpen = false;
    this.formChooserHighlightedIndex = -1;
    this.formEntryError = '';
  }

  public async beginSelectedFormEntry(): Promise<void> {
    const form = this.selectedForm;
    if (!form || this.formEntryLoading || this.formEntrySaving) return;

    this.selectedFormIdChanged();
    this.formChooserSuggestionsOpen = false;
    this.showFormChooserModal = false;
    this.showFormEntryModal = true;
    this.formEntryLoading = true;
    this.formEntryError = '';
    try {
      this.formEntryTags = await listTags('', false);
    } catch (error: unknown) {
      this.formEntryError = error instanceof Error
        ? error.message
        : this.i18n.tr('documents.form.formEntry.loadFailed');
    } finally {
      this.formEntryLoading = false;
    }
  }

  public selectedFormIdChanged(): void {
    const form = this.selectedForm;
    this.formValues = {};
    for (const field of form?.fields ?? []) {
      this.formValues[field.id] = field.dataType === 'boolean' ? false : '';
    }
    this._initialFormValues = { ...this.formValues };
    this.formEntryError = '';
  }

  public preventInvalidNumberKey(event: KeyboardEvent): void {
    if (event.isComposing || event.ctrlKey || event.metaKey) return;

    const allowedKeys = new Set([
      'Backspace',
      'Delete',
      'Tab',
      'Enter',
      'Escape',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ]);
    if (allowedKeys.has(event.key) || /^\d$/.test(event.key)) return;

    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      event.preventDefault();
      return;
    }

    const value = input.value;
    const start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? start;
    const remainingValue = value.slice(0, start) + value.slice(end);
    if ((event.key === '.' || event.key === ',') &&
        !remainingValue.includes('.') && !remainingValue.includes(',')) return;
    if (event.key === '-' && start === 0 && !remainingValue.includes('-')) return;

    event.preventDefault();
  }

  public onFormEntryNumberInput(event: Event, fieldId: string): void {
    this.formValues[fieldId] = this.sanitizeNumberInput(event);
  }

  public tagsForFormField(field: FormFieldDefinition): Tag[] {
    return this.formEntryTags
      .filter(tag => tag.groupId === field.tagGroupId)
      .sort((a, b) => a.position - b.position);
  }

  public async saveFormEntry(): Promise<void> {
    const form = this.selectedForm;
    if (!form || this.formEntrySaving) return;

    const submittedValues: Record<string, string | number | boolean | null> = {};
    for (const field of this.selectedFormFields) {
      const rawValue = this.formValues[field.id];
      if (field.dataType === 'number' &&
          rawValue !== '' &&
          rawValue !== null &&
          rawValue !== undefined) {
        const numberValue = Number(rawValue);
        if (!Number.isFinite(numberValue)) {
          this.formEntryError = this.i18n.tr(
            'documents.form.formEntry.invalidNumber',
            { name: field.name },
          );
          return;
        }
        submittedValues[field.name] = numberValue;
        continue;
      }

      if (field.dataType === 'tagSelect') {
        const tagId = typeof rawValue === 'string' && rawValue ? rawValue : null;
        const tag = tagId
          ? this.formEntryTags.find(item => item.id === tagId)
          : undefined;
        submittedValues[field.name] = tag?.name ?? null;
        continue;
      }

      submittedValues[field.name] = field.dataType === 'boolean'
        ? rawValue === true
        : (typeof rawValue === 'string' && rawValue.length > 0 ? rawValue : null);
    }

    this.formEntrySaving = true;
    this.formEntryError = '';
    try {
      const payload = {
        form: form.title,
        data: submittedValues,
      };
      const fileName = `form-${this.formFileSlug(form.title)}-${this.formFileTimestamp()}.json`;
      const file = new File([JSON.stringify(payload, null, 2)], fileName, {
        type: 'application/json',
      });
      const document = await createDocument({
        title: form.title,
        description: '',
        documentDate: new Date().toISOString(),
        tagNames: autoTagNamesForFormData(form, submittedValues),
        securityLevel: 1,
      });
      await uploadFile(document.id, file);
      this.saved();
    } catch (error: unknown) {
      this.formEntryError = error instanceof Error
        ? error.message
        : this.i18n.tr('documents.form.formEntry.submitFailed');
    } finally {
      this.formEntrySaving = false;
    }
  }

  private sanitizeNumberInput(event: Event): string {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return '';

    const rawValue = input.value;
    const normalizedInput = rawValue.replace(/,/g, '.');
    const negative = normalizedInput.startsWith('-');
    const normalized = normalizedInput.replace(/[^\d.]/g, '');
    const decimalIndex = normalized.indexOf('.');
    const value = decimalIndex < 0
      ? normalized
      : normalized.slice(0, decimalIndex + 1) +
        normalized.slice(decimalIndex + 1).replace(/\./g, '');
    const sanitized = `${negative ? '-' : ''}${value}`;

    if (input.value !== sanitized) input.value = sanitized;
    return sanitized;
  }

  private formFileSlug(title: string): string {
    const slug = title
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return slug || 'submission';
  }

  private formFileTimestamp(): string {
    return new Date().toISOString().replace(/[-:.TZ]/g, '').slice(0, 14);
  }
}
